import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  ButtonGroup,
  Button,
  Spinner,
} from "reactstrap"
import ReactApexChart from "react-apexcharts"
import { useSelector } from "react-redux"
import axios from "axios"
import PrimaryButton from "newcomponents/primary-button"

const ImpressionsChart = () => {
  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )
  const userData = useSelector(state => state.Login.userData)

  const [dataGranularity, setDataGranularity] = useState("monthly") // "daily" or "monthly"
  const [isLoading, setIsLoading] = useState(false)
  const mode = useSelector(state => state.Layout.layoutMode)
  const [chartData, setChartData] = useState({
    options: {
      colors: ["#2990DF"],
      chart: {
        toolbar: {
          show: false,
        },
      },
      xaxis: {
        categories: [], // Days or Months
        labels: {
          formatter: val => val,
        },
      },
      yaxis: {
        title: {
          text: "Impressions",
        },
      },
    },
    series: [
      {
        name: "Impressions",
        data: [], // Engagement data
      },
    ],
  })

  const fetchInstagramInsights = async () => {
    try {
      setIsLoading(true)

      if (dataGranularity === "daily") {
        // Fetch daily data for the past 2 weeks
        const now = new Date()
        const twoWeeksAgo = new Date()
        twoWeeksAgo.setDate(now.getDate() - 14)

        const since = Math.floor(twoWeeksAgo.getTime() / 1000)
        const until = Math.floor(now.getTime() / 1000)

        const response = await axios.get(
          `https://graph.facebook.com/v20.0/${activeOrganization.FacebookIntegrations[0].pageId}/insights?metric=page_impressions&period=day&since=${since}&until=${until}&access_token=${activeOrganization.FacebookIntegrations[0].pageToken}`,
        )

        const dailyData = response.data.data[0].values

        const dailyCategories = dailyData.map(entry =>
          new Date(entry.end_time).toLocaleDateString("default", {
            weekday: "short",
            day: "numeric",
            month: "short",
          }),
        )
        const dailyValues = dailyData.map(entry => entry.value)

        setChartData(prevData => ({
          ...prevData,
          options: {
            ...prevData.options,
            xaxis: {
              ...prevData.options.xaxis,
              categories: dailyCategories,
            },
          },
          series: [
            {
              name: "Impressions",
              data: dailyValues,
            },
          ],
        }))
      } else if (dataGranularity === "monthly") {
        // Fetch monthly data for the past 1 year
        const now = new Date()
        const oneYearAgo = new Date()
        oneYearAgo.setFullYear(now.getFullYear() - 1)

        const dateRanges = []
        let start = new Date(oneYearAgo)

        while (start < now) {
          const end = new Date(start)
          end.setDate(end.getDate() + 29)

          if (end > now) {
            end.setTime(now.getTime())
          }

          dateRanges.push({
            since: Math.floor(start.getTime() / 1000),
            until: Math.floor(end.getTime() / 1000),
          })

          start.setDate(start.getDate() + 30)
        }

        const monthlyData = {}

        for (const range of dateRanges) {
          const response = await axios.get(
            `https://graph.facebook.com/v20.0/${activeOrganization.FacebookIntegrations[0].pageId}/insights?metric=page_impressions&period=day&since=${range.since}&until=${range.until}&access_token=${activeOrganization.FacebookIntegrations[0].pageToken}`,
          )

          const dailyData = response.data.data[0].values

          dailyData.forEach(entry => {
            const month = new Date(entry.end_time).toLocaleString("default", {
              month: "short",
              year: "numeric",
            })

            if (!monthlyData[month]) {
              monthlyData[month] = 0
            }

            monthlyData[month] += entry.value
          })
        }

        const sortedMonths = Object.keys(monthlyData)
          .sort((a, b) => new Date(a) - new Date(b))
          .slice(-12)
        const engagementData = sortedMonths.map(month => monthlyData[month])

        setChartData(prevData => ({
          ...prevData,
          options: {
            ...prevData.options,
            xaxis: {
              ...prevData.options.xaxis,
              categories: sortedMonths,
            },
          },
          series: [
            {
              name: "Impressions",
              data: engagementData,
            },
          ],
        }))
      }
    } catch (err) {
      console.error(
        "Error fetching Instagram impresssions:",
        err.response?.data || err.message,
      )
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchInstagramInsights()
  }, [activeOrganization, dataGranularity]) // Re-fetch when granularity changes

  return (
    <React.Fragment>
      <Card className="br" style={{ minHeight: "350px" }}>
        {isLoading ? (
          <div
            style={{
              minHeight: "350px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Spinner
              size="lg"
              style={{
                color: mode === "dark" ? "#fff" : "#2990DF",
              }}
            />
          </div>
        ) : (
          <CardBody style={{ minHeight: "50vh" }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h4 className="card-title">Facebook Impressions</h4>
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ButtonGroup style={{}}>
                  {/* Monthly Button */}
                  <Button
                    size="sm"
                    onClick={() => setDataGranularity("monthly")}
                    style={{
                      minWidth: "150px",
                      height: "36px",
                      padding: "4px 10px",
                      fontWeight: 500,
                      fontSize: "13px",
                      lineHeight: 1.2,
                      transition: "all 0.2s ease",
                      backgroundColor:
                        dataGranularity === "monthly"
                          ? "#2990DF"
                          : "transparent",
                      color:
                        dataGranularity === "monthly"
                          ? "#fff"
                          : mode === "dark"
                            ? "#d1d5db"
                            : "#374151",
                      borderColor:
                        dataGranularity === "monthly"
                          ? "#2990DF"
                          : mode === "dark"
                            ? "#444"
                            : "#ccc",
                    }}
                  >
                    Monthly (1 Year)
                  </Button>

                  {/* Daily Button */}
                  <Button
                    size="sm"
                    onClick={() => setDataGranularity("daily")}
                    style={{
                      minWidth: "150px",
                      height: "36px",
                      padding: "4px 10px",
                      fontWeight: 500,
                      fontSize: "13px",
                      lineHeight: 1.2,
                      transition: "all 0.2s ease",
                      backgroundColor:
                        dataGranularity === "daily" ? "#2990DF" : "transparent",
                      color:
                        dataGranularity === "daily"
                          ? "#fff"
                          : mode === "dark"
                            ? "#d1d5db"
                            : "#374151",
                      borderColor:
                        dataGranularity === "daily"
                          ? "#2990DF"
                          : mode === "dark"
                            ? "#444"
                            : "#ccc",
                    }}
                  >
                    Daily (2 Weeks)
                  </Button>
                </ButtonGroup>
              </div>
            </div>

            <Row>
              {/* Chart */}
              <div>
                <ReactApexChart
                  options={chartData.options}
                  series={chartData.series}
                  type="line" // or "bar"
                  height="290"
                />
              </div>
            </Row>
          </CardBody>
        )}
      </Card>
    </React.Fragment>
  )
}

export default ImpressionsChart
